import { STOPWORDS } from './stopwords';
import type {
  AnalysisResult,
  MatchedTerm,
  ScoreBand,
  Term,
} from './types';

// Deterministic, in-browser resume ⇄ job matcher. No network, no AI, no
// persistence. The score is a keyword-coverage heuristic — it is advisory and
// never claims to predict an employer's real ATS decision.

/**
 * Pull candidate tokens from text. Preserves common technical spellings such as
 * `c++`, `node.js`, and `full-stack`, then trims stray punctuation.
 */
export function tokenize(text: string): string[] {
  const lowered = text.toLowerCase();
  const matches = lowered.match(/[\p{L}\p{N}][\p{L}\p{N}+#._-]*/gu) ?? [];
  const cleaned: string[] = [];
  for (const raw of matches) {
    const token = raw.replace(/^[#.]+/, '').replace(/[._-]+$/, '');
    if (token.length < 2) continue;
    if (/^\d+$/.test(token)) continue; // standalone numbers are noise here
    if (STOPWORDS.has(token)) continue;
    cleaned.push(token);
  }
  return cleaned;
}

/** Frequency table of normalized tokens, insertion-ordered for determinism. */
export function termFrequencies(text: string): Map<string, number> {
  const freq = new Map<string, number>();
  for (const token of tokenize(text)) {
    freq.set(token, (freq.get(token) ?? 0) + 1);
  }
  return freq;
}

export function uniqueTerms(text: string): Term[] {
  const terms: Term[] = [];
  for (const [normalized, count] of termFrequencies(text)) {
    terms.push({ normalized, display: normalized, count });
  }
  return terms;
}

function bandFor(score: number): ScoreBand {
  if (score >= 70) return 'strong';
  if (score >= 40) return 'partial';
  return 'weak';
}

/**
 * Coverage heuristic: fraction of distinct job terms that also appear in the
 * resume. Repeated job terms count once (set-based), so stuffing a job
 * description with the same word cannot inflate the score.
 */
export function analyzeMatch(resumeText: string, jobText: string): AnalysisResult {
  const resumeFreq = termFrequencies(resumeText);
  const jobTerms = uniqueTerms(jobText);

  const matched: MatchedTerm[] = [];
  const missing: Term[] = [];

  for (const jobTerm of jobTerms) {
    const resumeCount = resumeFreq.get(jobTerm.normalized) ?? 0;
    if (resumeCount > 0) {
      matched.push({ ...jobTerm, jobCount: jobTerm.count, resumeCount });
    } else {
      missing.push(jobTerm);
    }
  }

  const jobTermCount = jobTerms.length;
  const rawScore = jobTermCount === 0 ? 0 : matched.length / jobTermCount;
  const score = Math.round(rawScore * 100);

  // Prioritise job terms that appear often; ties keep source order.
  matched.sort(byFrequencyDesc);
  missing.sort(byFrequencyDesc);

  const suggestions = buildSuggestions(missing);

  return {
    score,
    band: bandFor(score),
    jobTermCount,
    matched,
    missing,
    suggestions,
  };
}

function byFrequencyDesc<T extends { count: number }>(a: T, b: T): number {
  return b.count - a.count;
}

const MAX_SUGGESTIONS = 8;

function buildSuggestions(missing: Term[]): string[] {
  if (missing.length === 0) {
    return [
      'Every key term in the job description appears somewhere in your resume. Re-check that each mention reflects real, verifiable experience before sending.',
    ];
  }
  const topTerms = missing.slice(0, MAX_SUGGESTIONS).map((t) => t.normalized);
  const list = topTerms.join(', ');
  const suggestions = [
    `Consider where you can add verifiable evidence for: ${list}. Only add these if they genuinely match your experience — never invent them.`,
  ];
  if (missing.length > MAX_SUGGESTIONS) {
    suggestions.push(
      `${missing.length - MAX_SUGGESTIONS} more job terms are not yet covered; review the full missing list.`,
    );
  }
  suggestions.push(
    'Mirror the exact wording the employer uses for skills you truly have, so both ATS parsing and human reviewers recognize it.',
  );
  return suggestions;
}
